import { createApi } from '@reduxjs/toolkit/query/react';
import baseApi from './base';
import paginationParams from 'utils/pagination-params';
import { QUESTION_TYPE } from 'constants';

function reorderIndices(data) {
  const entries = Object.entries(data);
  entries.sort((a, b) => a[1].idx - b[1].idx);

  const result = {};

  entries.forEach((entry, newIdx) => {
    const [key, value] = entry;
    result[key] = {
      ...value,
      idx: newIdx
    };
  });

  return result;
}

export const list = 'LIST_REQUISITIONS';
export const single = 'SINGLE_REQUISITION';
export const applicant = 'APPLICANT_DATA';

export const requisitionsApi = createApi({
  reducerPath: 'requisitionsApi',
  baseQuery: baseApi,
  tagTypes: [list, single, applicant],
  endpoints: (builder) => ({
    generateRequisitionUid: builder.query({
      query: () => `/requisitions/generate-uid`
    }),
    createRequisition: builder.mutation({
      query: ({ lob, ...requisition }) => ({
        url: '/requisitions/create',
        method: 'POST',
        body: { ...requisition, ...(lob && { lobId: lob.id }) }
      }),
      async onQueryStarted({ lob }, { dispatch, queryFulfilled }) {
        try {
          const { data: created } = await queryFulfilled;
          dispatch(
            requisitionsApi.util.updateQueryData('listRequisitions', undefined, (draft) => {
              draft.data.items.unshift({ ...created.data, ...(lob?.name && { lobId: lob }) });
            })
          );
        } catch (err) {
          console.error('Error updating cache:', err);
        }
      }
    }),
    listRequisitions: builder.query({
      query: (params) => ({
        url: '/requisitions/list',
        params
      }),
      providesTags: [list],
      ...paginationParams
    }),
    updateRequisition: builder.mutation({
      query: ({ lobIdFilter: _, requisitionId, lob, ...attributes }) => ({
        url: `/requisitions/update/${requisitionId}`,
        method: 'PUT',
        body: { ...attributes, ...(lob && { lobId: lob.id }) }
      }),
      invalidatesTags: [single],
      async onQueryStarted({ lobIdFilter, requisitionId, lob }, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedRequisition } = await queryFulfilled;

          dispatch(
            requisitionsApi.util.updateQueryData('listRequisitions', lobIdFilter ? { lob: lobIdFilter } : undefined, (draft) => {
              const requisition = draft.data.items.find((item) => item.id === requisitionId);
              const {
                name,
                requisitionUid,
                jobTitle,
                location,
                internalJobCode,
                type,
                workEnvironment,
                startDate,
                publishedAt,
                statusChangedAt,
                status,
                requiredHC,
                clientSubmissionDate,
                numHired,
                reasonForCancellation
              } = updatedRequisition.data;

              if (requisition) {
                Object.assign(requisition, {
                  name,
                  requisitionUid,
                  jobTitle: {
                    internalJobCode,
                    jobTitle,
                    location,
                    type,
                    workEnvironment
                  },
                  startDate,
                  publishedAt,
                  statusChangedAt,
                  status,
                  requiredHC,
                  clientSubmissionDate,
                  numHired,
                  reasonForCancellation,
                  ...(lob?.name && { lobId: lob })
                });
              }
            })
          );
        } catch (err) {
          console.error('Error updating cache:', err);
        }
      }
    }),
    deleteRequisition: builder.mutation({
      query: (requisitionId) => ({
        url: `/requisitions/delete/${requisitionId}`,
        method: 'DELETE'
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            requisitionsApi.util.updateQueryData('listRequisitions', undefined, (draft) => {
              const idx = draft.data.items.findIndex((item) => item.id === id);
              if (idx > -1) {
                draft.data.items.splice(idx, 1);
              }
            })
          );
        } catch (err) {
          console.error('Error updating cache:', err);
        }
      }
    }),
    getRequisitionById: builder.query({
      query: (requisitionId) => `/requisitions/get/${requisitionId}`,
      providesTags: [single]
    }),
    incrementRequisitionVisit: builder.mutation({
      query: (requisitionId) => ({
        url: `/requisitions/visit/increment/${requisitionId}`,
        method: 'PUT'
      })
    }),
    viewRequisition: builder.query({
      query: (requisitionId) => `/requisitions/view/${requisitionId}`
    }),
    viewRequisitionApplicant: builder.query({
      query: ({ applicantId, archived }) => ({
        url: `/requisitions/applicant/view/${applicantId}`,
        ...(archived && { params: { archived: 1 } })
      }),
      transformResponse: (res) => {
        if (res.data?.qualificationQuestions?.length) {
          res.data.qualificationQuestions = res.data.qualificationQuestions.filter((q) => {
            if (q.id && q.type !== QUESTION_TYPE.TEXT && !q.answers?.length) {
              delete res.data.qualificationQuestionResults[q.id];
              res.data.qualificationQuestionResults = reorderIndices(res.data.qualificationQuestionResults);
              return;
            }
            return true;
          });
        }
        return res;
      },
      providesTags: [applicant]
    }),
    submitResume: builder.mutation({
      query: ({ requisitionId, ...payload }) => ({
        url: `/requisitions/resume-profile/submit/${requisitionId}`,
        method: 'PUT',
        body: payload
      }),
      invalidatesTags: [applicant],
      async onQueryStarted({ requisitionId }, { dispatch, getState, queryFulfilled }) {
        try {
          const { data: updatedRequisition } = await queryFulfilled;

          if (getState().auth?.isLoggedIn) {
            dispatch(
              requisitionsApi.util.updateQueryData('listRequisitions', undefined, (draft) => {
                const requisition = draft.data.items.find((profile) => profile.id === requisitionId);
                if (requisition) {
                  Object.assign(requisition, updatedRequisition.data);
                }
              })
            );
          }
        } catch (err) {
          console.error('Error updating cache:', err);
        }
      }
    }),
    submitQualificationQuestions: builder.mutation({
      query: ({ applicantId, ...body }) => ({
        url: `/requisitions/qualification-question/submit/${applicantId}`,
        method: 'PUT',
        body
      }),
      invalidatesTags: [applicant],
      async onQueryStarted({ requisitionId }, { dispatch, getState, queryFulfilled }) {
        try {
          const { data: updatedRequisition } = await queryFulfilled;

          if (getState().auth?.isLoggedIn) {
            dispatch(
              requisitionsApi.util.updateQueryData('listRequisitions', undefined, (draft) => {
                const requisition = draft.data.items.find((profile) => profile.id === requisitionId);
                if (requisition) {
                  Object.assign(requisition, updatedRequisition.data);
                }
              })
            );
          }
        } catch (err) {
          console.error('Error updating cache:', err);
        }
      }
    }),
    submitVideo: builder.mutation({
      query: ({ applicantId, ...body }) => ({
        url: `/requisitions/video-question/submit/${applicantId}`,
        method: 'PUT',
        body
      }),
      invalidatesTags: [applicant],
      async onQueryStarted({ requisitionId }, { dispatch, getState, queryFulfilled }) {
        try {
          const { data: updatedRequisition } = await queryFulfilled;

          if (getState().auth?.isLoggedIn) {
            dispatch(
              requisitionsApi.util.updateQueryData('listRequisitions', undefined, (draft) => {
                const requisition = draft.data.items.find((profile) => profile.id === requisitionId);
                if (requisition) {
                  Object.assign(requisition, updatedRequisition.data);
                }
              })
            );
          }
        } catch (err) {
          console.error('Error updating cache:', err);
        }
      }
    }),
    submitAssessment: builder.mutation({
      query: ({ applicantId, ...body }) => ({
        url: `/requisitions/typing-assessment/submit/${applicantId}`,
        method: 'PUT',
        body
      }),
      invalidatesTags: [applicant],
      async onQueryStarted({ requisitionId }, { dispatch, getState, queryFulfilled }) {
        try {
          const { data: updatedRequisition } = await queryFulfilled;

          if (getState().auth?.isLoggedIn) {
            dispatch(
              requisitionsApi.util.updateQueryData('listRequisitions', undefined, (draft) => {
                const requisition = draft.data.items.find((profile) => profile.id === requisitionId);
                if (requisition) {
                  Object.assign(requisition, updatedRequisition.data);
                }
              })
            );
          }
        } catch (err) {
          console.error('Error updating cache:', err);
        }
      }
    })
  })
});

export const {
  useCreateRequisitionMutation,
  useUpdateRequisitionMutation,
  useDeleteRequisitionMutation,
  useGetRequisitionByIdQuery,
  useIncrementRequisitionVisitMutation,
  useViewRequisitionQuery,
  useViewRequisitionApplicantQuery,
  useListRequisitionsQuery,
  useGenerateRequisitionUidQuery,
  useSubmitResumeMutation,
  useSubmitQualificationQuestionsMutation,
  useSubmitVideoMutation,
  useSubmitAssessmentMutation
} = requisitionsApi;
